/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from 'react';
import { Box, Button, FormControlLabel, FormGroup, Switch, Typography } from '@mui/material';
import { Download } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { ToolsDamInformation } from '../../components/custom';
import { LayoutBase } from '../../layouts';
import { PSBServices } from '../../services/api/psb/psb';
import { CustomAlert } from '../../components/shared';

import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';
import { getUniqueFileName } from '../../services/helpers/helpers';

type getPSBProps = {
    tomos: number[],
    volumes: number[],
}
const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};

export const Psb = () => {
    const { id } = useParams();
    const [alertInfo, setAlertInfo] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    const [values, setValues] = useState<getPSBProps>({
        tomos: [],
        volumes: [],
    });


    const handleSwitchChange = (type: 'tomos' | 'volumes', value: number) => {
        setValues(prev => {
            const currentItems = prev[type];
            const newItems = currentItems.includes(value)
                ? currentItems.filter(item => item !== value)
                : [...currentItems, value];


            if (type === 'tomos' && newItems.includes(1) && newItems.includes(2)) {
                return {
                    ...prev,
                    tomos: newItems,
                    volumes: [...prev.volumes, 1],
                };
            }


            if (type === 'tomos' && !newItems.includes(1) && !newItems.includes(2)) {
                return {
                    ...prev,
                    tomos: newItems,
                    volumes: prev.volumes.filter(item => item !== 1),
                };
            }

            return { ...prev, [type]: newItems };
        });
    };

    const handleDownload = async () => {
        setAlertInfo({ open: false, message: '', severity: 'info' });
        if (!id) {
            setAlertInfo({ open: true, message: 'ID da barragem não encontrado para download.', severity: 'error' });
            return;
        }
        if (values.tomos.length === 0 && values.volumes.length === 0) {
            setAlertInfo({ open: true, message: 'Selecione pelo menos um Tomo ou Volume para baixar.', severity: 'warning' });
            return;
        }

        try {
            const response = await PSBServices.getPSB(id, values);

            if (!(response.data instanceof Blob)) {
                setAlertInfo({ open: true, message: `Erro: ${response.message || 'Resposta da API não é um Blob.'}`, severity: 'error' });
                return;
            }

            const fileBlob = response.data;
            const originalFileName = 'PSB.zip';
            const contentType = 'application/zip'; 

            if (Capacitor.isNativePlatform()) {
                const permissionStatus: any = await Filesystem.checkPermissions();
                const publicStorageNotGranted = permissionStatus.publicStorage !== 'granted';
                const mediaNotGranted = permissionStatus.media !== 'granted';

                if (publicStorageNotGranted && mediaNotGranted) {
                    const requestResult: any = await Filesystem.requestPermissions();
                    if (requestResult.publicStorage !== 'granted' && requestResult.media !== 'granted') {
                        setAlertInfo({ open: true, message: 'Permissão de armazenamento necessária para baixar o arquivo!', severity: 'error' });
                        return;
                    }
                }

                const base64Data = await blobToBase64(fileBlob);
                const base64Content = base64Data.split(',')[1];

                const directory = Directory.Documents;
                const finalFileName = await getUniqueFileName(originalFileName, directory);

                await Filesystem.writeFile({
                    path: finalFileName,
                    data: base64Content,
                    directory: directory,
                    recursive: true,
                });

                setAlertInfo({ open: true, message: `Arquivo "${finalFileName}" salvo em Documents!`, severity: 'success' });
            } else {
                const url = window.URL.createObjectURL(fileBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = originalFileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                setAlertInfo({ open: true, message: `Download de "${originalFileName}" iniciado!`, severity: 'success' });
            }
        } catch (error: any) {
            let errorMessage = 'Não foi possível baixar o arquivo. Tente novamente.';
            if (Capacitor.isNativePlatform()) {
                if (error.message && error.message.includes('Permission denied')) {
                    errorMessage = 'Permissão de armazenamento negada. Habilite nas configurações do app.';
                } else if (error.message) {
                    errorMessage = `Erro ao baixar o arquivo: ${error.message}.`;
                }
            } else if (error.message) {
                errorMessage = `Erro ao baixar o arquivo: ${error.message}.`;
            }
            setAlertInfo({ open: true, message: errorMessage, severity: 'error' });
        }
    };


    return (
        <LayoutBase
            tollBar={<ToolsDamInformation />}
            title='PSB - Plano de Segurança de Barragens'
        >
            <Typography variant='h5' align='center'>PSB - Plano de Segurança de Barragens</Typography>
            <Box sx={{ padding: 4, display: 'flex', flexDirection: 'row', justifyContent: 'space-around' }}>
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', flexDirection: 'column', gap: 2, p: 2 }}>
                    <div>
                        <Typography variant='h6'>Itens inclusos no PSB</Typography>
                        <Typography variant='body2'>Esses itens podem ser removidos tendo em vista deixar o arquivo mais leve</Typography>
                    </div>
                    <FormGroup>
                        <FormControlLabel
                            control={<Switch checked={values.tomos.includes(1)} onChange={() => handleSwitchChange('tomos', 1)} />}
                            label="Tomo I"
                        />
                        <FormControlLabel
                            control={<Switch checked={values.tomos.includes(2)} onChange={() => handleSwitchChange('tomos', 2)} />}
                            label="Tomo II"
                        />
                        <FormControlLabel
                            control={<Switch checked={values.volumes.includes(1)} onChange={() => handleSwitchChange('volumes', 1)} />}
                            label="Volume I"
                        />
                        <FormControlLabel
                            control={<Switch checked={values.volumes.includes(2)} onChange={() => handleSwitchChange('volumes', 2)} />}
                            label="Volume II"
                        />
                        <FormControlLabel
                            control={<Switch checked={values.volumes.includes(3)} onChange={() => handleSwitchChange('volumes', 3)} />}
                            label="Volume III"
                        />
                    </FormGroup>
                </Box>
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
                    <Typography variant='h6'>Download do arquivo</Typography>
                    <Button
                        variant='contained'
                        startIcon={<Download />}
                        onClick={handleDownload}
                        sx={{ borderRadius: '25px' }}
                    >
                        Download
                    </Button>
                </Box>
            </Box>
            {alertInfo.open &&
                <CustomAlert
                    message={alertInfo.message}
                    severity={alertInfo.severity}
                />
            }
        </LayoutBase>
    );
};
